import React, { useState, useCallback, useEffect } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '@/components/modal/Modal';
import { UnsavedChangesModal } from '@/components/unsavedChanges/UnsavedChangesModal';
import { ModalProps } from '@/components/modal/useModal';
import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { Form } from '@/components/form/Form';
import { TextField } from '@/components/form/fields/text/TextInput';
import { DateField } from '@/components/form/fields/date/DateInput';
import { YesNoField } from '@/components/form/fields/yesNo/YesNoInput';
import { TextAreaField } from '@/components/form/fields/text/TextAreaInput';
import { AddressBookSelector } from '@/components/form/fields/address/AddressBookSelector';
import { AddressRequest } from '@/services/api/contracts/address';
import { Card } from '@/components/sections/Card';
import { useCreateServiceRequest } from '@/services/api/hooks/serviceRequests';
import { useUser } from '@/services/authentication/hooks/user';
import { useCompany, useMspDetailsForCustomer } from '@/services/api/hooks/companies';
import { toast } from 'sonner';

const CollectionFormSchema = z.object({
  fullName: z.string().min(1, 'Required'),
  workEmail: z.string().email('Invalid email').or(z.literal('na')),
  noCorporateEmail: z.boolean().default(false),
  targetDate: z.date(),
  isPrimaryContact: z.boolean(),
  personalEmail: z.string().email('Invalid email').optional().or(z.literal('')),
  mobileNumber: z.string().optional().or(z.literal('')),
  contactName: z.string().optional().or(z.literal('')),
  contactEmail: z.string().email('Invalid email').optional().or(z.literal('')),
  contactMobile: z.string().optional().or(z.literal('')),
  customerReference: z.string().optional(),
  notes: z.string().optional(),
  address: AddressRequest.omit({ name: true })
});

type CollectionFormFields = z.infer<typeof CollectionFormSchema>;

interface OffboardUserCollectionModalProps extends ModalProps {
  mspId: string;
  customerId: string;
  customerName: string;
  hardwareSelections: Record<string, number>;
  inferredServices: Array<{ rateCard: any; quantity: number; labels: string[] }>;
  runningTotal: number;
  devicesToBeCollected: string[];
  fullName?: string;
  workEmail?: string;
  onSuccessCallback: () => void;
}

export const OffboardUserCollectionModal: React.FC<OffboardUserCollectionModalProps> = ({ 
  mspId, 
  customerId, 
  customerName, 
  hardwareSelections, 
  inferredServices, 
  runningTotal, 
  devicesToBeCollected, 
  fullName,
  workEmail,
  onSuccessCallback,
  ...props 
}) => {
  const [errorMessage, setErrorMessage] = useState('');
  const createServiceRequest = useCreateServiceRequest();
  const user = useUser();
  const [partner] = useMspDetailsForCustomer(mspId, customerId);
  const [company] = useCompany(mspId, customerId);

  const methods = useForm<CollectionFormFields>({
    resolver: zodResolver(CollectionFormSchema),
    defaultValues: {
      isPrimaryContact: true,
      fullName: fullName || '',
      workEmail: workEmail || '',
      noCorporateEmail: false,
      personalEmail: '',
      mobileNumber: '',
      contactName: '',
      contactEmail: '',
      contactMobile: '',
      customerReference: '',
      notes: ''
    }
  });

  useEffect(() => {
    if (props.isOpen) {
      methods.reset({
        isPrimaryContact: true,
        fullName: fullName || '',
        workEmail: workEmail || '',
        noCorporateEmail: false,
        personalEmail: '',
        mobileNumber: '',
        contactName: '',
        contactEmail: '',
        contactMobile: '',
        customerReference: '',
        notes: ''
      });
    }
  }, [props.isOpen, fullName, workEmail, methods]);

  const isPrimaryContact = methods.watch('isPrimaryContact');
  const noCorporateEmail = methods.watch('noCorporateEmail');

  useEffect(() => {
    if (noCorporateEmail) {
      methods.setValue('workEmail', 'na', { shouldValidate: true, shouldDirty: false });
    } else if (methods.getValues('workEmail') === 'na') {
      methods.setValue('workEmail', '', { shouldValidate: true, shouldDirty: false });
    }
  }, [noCorporateEmail, methods]);

  const handleAddressChange = useCallback((addressPayload: any) => {
    methods.setValue('address', addressPayload, { shouldValidate: true, shouldDirty: true });
  }, [methods]);

  const onSubmit = async (data: CollectionFormFields) => {
    setErrorMessage('');
    
    // Validate contact details correctly based on toggle
    if (data.isPrimaryContact) {
      if (!data.personalEmail) {
        methods.setError('personalEmail', { message: 'Required' });
        return;
      }
      if (!data.mobileNumber) {
        methods.setError('mobileNumber', { message: 'Required' });
        return;
      }
    } else {
      if (!data.contactName) {
        methods.setError('contactName', { message: 'Required' });
        return;
      }
      if (!data.contactEmail) {
        methods.setError('contactEmail', { message: 'Required' });
        return;
      }
      if (!data.contactMobile) {
        methods.setError('contactMobile', { message: 'Required' });
        return;
      }
    }

    const personalEmail = data.isPrimaryContact ? data.personalEmail! : data.contactEmail!;
    const mobileNumber = data.isPrimaryContact ? data.mobileNumber! : data.contactMobile!;

    let extraNotes = '';
    if (!data.isPrimaryContact && data.contactName) {
      extraNotes += `Alternative Contact: ${data.contactName}\n`;
    }
    if (data.customerReference) {
      extraNotes += `Billing Ref: ${data.customerReference}\n`;
    }
    const finalNotes = data.notes ? `${extraNotes}\n${data.notes}` : extraNotes.trim();

    const collectionAddress = [data.address.line1, data.address.line2, data.address.city, data.address.state, data.address.postcode].filter(Boolean).join(', ');

    const finalCustomerReference = data.customerReference?.trim() || data.fullName || 'No Reference Provided';

    // Final Payload for Ledger
    const logisticsDetails = {
      ...data,
      customerReference: finalCustomerReference,
      targetDate: data.targetDate ? new Date(data.targetDate).toISOString() : undefined,
      address: {
        ...data.address,
        name: 'Collection Address'
      }
    };

    const details = {
      requestType: 'OffboardUser',
      requestedBy: user?.email || 'Unknown',
      mspId,
      mspName: partner?.name || '',
      customerId,
      customerName: company?.name || '',
      raisedDateTime: new Date().toISOString(),
      customerReference: finalCustomerReference,
      submissionSource: 'DaaS Portal UI',
      totalEstimatedCost: runningTotal,
      haloClientId: company?.haloClientId || null,
      haloGeneralUserId: company?.haloGeneralUserId || null
    };

    try {
      await createServiceRequest.mutateAsync({
        params: { mspId, customerId },
        body: {
          requestedBy: user?.email || 'Unknown',
          requestType: 'OffboardUser',
          customerReference: finalCustomerReference,
          payload: {
            details,
            logisticsDetails,
            collectedItems: {
              devicesToBeCollected,
              hardwareSelections
            },
            inferredServices: inferredServices.map((s: any) => ({
              rateCardId: s.rateCard?.id,
              name: s.name || s.rateCard?.name,
              quantity: s.quantity,
              price: s.rateCard?.price?.price
            })),
            totalCost: runningTotal
          }
        }
      });

      toast.success('Service Request Logged Successfully');
      onSuccessCallback();
      props.close();
    } catch (e) {
      toast.error('Failed to log request. Please try again.');
    }
  };

  const { isDirty } = methods.formState;
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);

  const handleClose = () => {
    if (isDirty) {
      setShowUnsavedWarning(true);
    } else {
      props.close();
    }
  };

  const guardedProps = { ...props, close: handleClose };

  const isLoading = createServiceRequest.isLoading;

  return (
    <>
      <Modal className="md:max-w-5xl lg:max-w-6xl flex flex-col h-[90vh]" {...guardedProps}>
      <Card className="w-full h-full flex flex-col overflow-hidden bg-white">
        <div className="p-8 pb-4 shrink-0">
          <Modal.Controls {...guardedProps}>
            <h1 className="text-2xl font-semibold">Offboard User</h1>
          </Modal.Controls>
          <p className="text-gray-600">Please provide the logistical details for this user offboard.</p>
        </div>
        
        <Form methods={methods} onSubmit={onSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto px-8 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Left Column: The Receipt & Admin Details */}
            <div className="space-y-6">
              <div className="flex flex-col border border-gray-200 rounded bg-gray-50 p-6 h-fit">
                <h3 className="text-lg font-bold border-b pb-3 mb-4">Collection Summary</h3>
                
                {devicesToBeCollected && devicesToBeCollected.length > 0 && (
                  <div className="mb-4 bg-white p-3 rounded border border-gray-200 text-sm">
                    <p className="font-semibold text-gray-800 mb-2 border-b pb-1">Hardware to Collect:</p>
                    <ul className="space-y-1 text-gray-600 list-disc list-inside">
                      {devicesToBeCollected.map((d, i) => (
                        <li key={i}>{d}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="space-y-4 flex-1 mt-4">

                  {inferredServices.map((item: any, index: number) => {
                    const isInventoryItem = item.isInventoryItem;
                    return (
                      <div key={index} className="flex justify-between items-start text-sm border-b border-gray-100 last:border-b-0 pb-2">
                        <div className="pr-4">
                          <span className="font-medium">{item.quantity}x</span> {item.name || item.rateCard?.name || 'Service'}
                          {item.hasLifecyclePack && (
                            <span className="italic text-green-600 text-xs block mt-1">Legacy Lifecycle Pack Detected - Cost May Not Apply</span>
                          )}
                        </div>
                        <div className="font-medium whitespace-nowrap">
                          {isInventoryItem ? (
                            <span className="italic">From Inv</span>
                          ) : (
                            new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(((item.rateCard?.price?.price || 0) / 100) * item.quantity)
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {inferredServices.length === 0 && (
                    <div className="text-gray-500 italic text-sm">No items selected.</div>
                  )}
                </div>
                
                <div className="flex justify-between items-center pt-4 mt-6 border-t font-bold text-lg">
                  <span>Estimated Total</span>
                  <span>{new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(runningTotal || 0)}</span>
                </div>
              </div>

              <div className="border p-4 rounded space-y-4">
                  <div>
                    <TextField id="customerReference" label="Customer Reference" placeholder="This will appear on your invoice..." optional={true} />
                    <p className="text-xs text-gray-500 italic mt-1">If no reference is given, the user's name will be used</p>
                  </div>
              </div>

              <div className="border p-4 rounded space-y-4">
                <TextAreaField id="notes" label="Notes / Special Requirements" optional={true} />
              </div>
            </div>

          {/* Right Column: Logistics & Data Entry */}
          <div className="space-y-6">
            <div className="border p-4 rounded space-y-4">
              <h4 className="text-lg font-bold">User Details</h4>
                <TextField id="fullName" label="User's Name" placeholder="this is the person leaving the organisation" />
                <div className={noCorporateEmail ? 'opacity-50 pointer-events-none' : ''}>
                  <TextField id="workEmail" label="User's Corporate Email" placeholder="this identifies them in our system" />
                </div>
                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                  <input type="checkbox" {...methods.register('noCorporateEmail')} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  No Corporate Email
                </label>
              </div>

              <div className="border p-4 rounded space-y-4">
                <h4 className="text-lg font-bold">Contact Details</h4>
                <YesNoField id="isPrimaryContact" label="Is the user the primary contact for this service?" />
                {isPrimaryContact ? (
                  <div className="space-y-4">
                    <TextField id="personalEmail" label="User's personal email" placeholder="used to communicate with the user after termination date" />
                    <TextField id="mobileNumber" label="User's personal mobile number" placeholder="used to communicate with the user after termination date" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <TextField id="contactName" label="Contact Name" />
                    <TextField id="contactEmail" label="Contact Email" />
                    <TextField id="contactMobile" label="Contact Mobile" />
                  </div>
                )}
              </div>

              <div className="border p-4 rounded space-y-4">
                <h4 className="text-lg font-bold">Collection Address</h4>
                <AddressBookSelector 
                  mspId={mspId}
                  customerId={customerId}
                  onChange={handleAddressChange}
                />
                {methods.formState.errors.address && (
                  <p className="text-red-500 text-sm mt-1">Please select or provide a valid address.</p>
                )}
                <DateField
                  id="targetDate"
                  label="User Termination Date"
                  excludeDates={[new Date().toDateString()]}
                  excludePastDates={true}
                />
              </div>

              {errorMessage && (
                <div className="text-red-600 text-sm bg-red-50 p-3 rounded border border-red-200">
                  {errorMessage}
                </div>
              )}
            </div>
          </div>
          </div>

          <div className="flex justify-end p-6 border-t bg-gray-50 shrink-0">
            <Button variant={ButtonVariant.Ghost} onClick={guardedProps.close} className="mr-4" disabled={isLoading}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant={ButtonVariant.Primary}
              disabled={isLoading}
            >
              {isLoading ? 'Submitting...' : 'Confirm Offboard & Submit'}
            </Button>
          </div>
        </Form>
      </Card>
    </Modal>
    <UnsavedChangesModal
      isOpen={showUnsavedWarning}
      open={() => setShowUnsavedWarning(true)}
      close={() => setShowUnsavedWarning(false)}
      onCancelCallback={() => setShowUnsavedWarning(false)}
      onConfirmCallback={() => {
        setShowUnsavedWarning(false);
        props.close();
      }}
      title="Abandon Request"
      message="Your request is partially complete. Are you sure you want to leave? Your progress will be lost."
      confirmText="Yes, Abandon"
      confirmColorClass="bg-secondary-600 hover:bg-secondary-700 text-white"
      cancelText="No, Continue"
      cancelColorClass="bg-primary-700 hover:bg-primary-800 text-white"
    />
    </>
  );
};
